/**
 * Tour guidé de la page d'évaluation (SAAS-740).
 *
 * @remarks
 * Déclenché par le switch header, qui ne s'affiche que sur le contexte
 * `tech-test-evaluation`. Il présente les outils de l'épreuve.
 *
 * Ancres :
 *  - icônes d'onglets → `[data-tour="panel-tab-<id>"]` (posé une fois dans
 *    `PanelTabs`), persistantes tant que le panneau est ouvert ;
 *  - sections du panneau gauche « Documents » → `[data-tour="eval-*"]` (posé
 *    dans `EvaluationDocumentExplorerView`), visibles car `prepare` force
 *    l'onglet Documents actif ;
 *  - champs de l'écran de soumission → `[data-tour="eval-submit-*"]` (posé dans
 *    `EvaluationSubmissionView`), montés seulement quand l'onglet droit
 *    « Soumettre » est actif — d'où `activeTab` sur ces étapes (SAAS-952).
 */

import { CONTEXTS, type Context } from '@/app/registry';
import { useLayoutStore } from '@/app/stores/use-layout-store';
import { TOUR_IDS, type GuidedTourStep, type TourDefinition } from './tour-types';

/** Contexte de la page d'évaluation (attempt en cours). */
export const EVALUATION_CONTEXT: Context = CONTEXTS.TECH_TEST_EVALUATION;

/** Onglet gauche portant `EvaluationDocumentExplorerView` (cf. manifest tech-tests). */
const LEFT_DOCUMENTS_TAB = 'evaluation-documents';

/** Onglet droit portant `EvaluationSubmissionView` (cf. manifest tech-tests). */
const SUBMISSION_TAB = 'submission';

const EVALUATION_STEPS: readonly GuidedTourStep[] = [
  {
    selector: '[data-tour="eval-workspace"]',
    title: 'Mon espace',
    description:
      'Crée et organise ici tes documents de travail. Tu pourras les soumettre au moment de rendre ton attempt.',
  },
  {
    selector: '[data-tour="panel-tab-evaluation-bookmarks"]',
    title: 'Marque-pages',
    description: 'Marque une section importante pour la retrouver en un clic pendant l’épreuve.',
  },
  {
    selector: '[data-tour="eval-phase-docs"]',
    title: 'Documents de phase & ressources',
    description:
      'Les consignes de l’épreuve. Ouvre un document de phase (ou une ressource, juste en dessous) pour l’afficher au centre.',
  },
  {
    selector: '[data-tour="panel-tab-evaluation-notes"]',
    title: 'Tes notes',
    description: 'Ce que tu annotes dans les documents se retrouve ici, dans le panneau Notes.',
  },
  {
    selector: '[data-tour="panel-tab-evaluation-toc"]',
    title: 'Structure du document',
    description: 'Navigue rapidement dans un document long grâce à son sommaire.',
  },
  {
    selector: '[data-tour="panel-tab-evaluation-pomodoro"]',
    title: 'Minuteur Focus',
    description: 'Lance un minuteur (Pomodoro) pour rythmer ton travail et rester concentré.',
  },
  {
    selector: '[data-tour="panel-tab-submission"]',
    title: 'Rendre ton travail',
    description:
      'Tout le rendu se passe ici, dans l’onglet « Soumettre » : dépôt Git, documents et notes.',
    activeTab: { side: 'right', id: SUBMISSION_TAB },
  },
  {
    selector: '[data-tour="eval-submit-git-url"]',
    title: 'Le lien de ton dépôt',
    description:
      'Colle ici l’URL de ton dépôt Git (GitHub, GitLab…). Vérifie qu’il est accessible : c’est ce dépôt qui sera cloné pour l’évaluation.',
    activeTab: { side: 'right', id: SUBMISSION_TAB },
    optional: true,
  },
  {
    selector: '[data-tour="eval-submit-verify"]',
    title: 'Vérifier l’accès au dépôt',
    description:
      'Clique sur ce bouton pour contrôler l’accès au dépôt et choisir la branche à évaluer. Tant que cette vérification n’a pas réussi, la soumission reste bloquée.',
    activeTab: { side: 'right', id: SUBMISSION_TAB },
    optional: true,
  },
  {
    selector: '[data-tour="eval-submit-button"]',
    title: 'Soumettre',
    description:
      'Une fois le dépôt vérifié — et tes documents joints si besoin — envoie ton travail. Une confirmation te laisse choisir les documents de ton espace à faire évaluer. La soumission est définitive.',
    activeTab: { side: 'right', id: SUBMISSION_TAB },
    optional: true,
  },
];

/**
 * Garantit que les deux barres d'onglets sont montées (un panneau replié les
 * démonte) et affiche l'espace documents. Précédent : PhaseDocPane fait pareil.
 */
function prepareEvaluationLayout(): void {
  const layout = useLayoutStore.getState();
  layout.setLeftDrawerOpen(true);
  layout.setRightDrawerOpen(true);
  layout.setLeftActiveTab(LEFT_DOCUMENTS_TAB);
}

export const EVALUATION_TOUR: TourDefinition = {
  id: TOUR_IDS.EVALUATION,
  steps: EVALUATION_STEPS,
  prepare: prepareEvaluationLayout,
};
